import type { SidebarGroup } from './sidebar-definition.types';

/**
 * The i18n key for each sidebar GROUP heading.
 *
 * Hoisted out of `sidebar-definitions.ts` when that file crossed the ~300 line
 * ceiling (`.claude/rules/architecture.md`), continuing the split its sibling
 * types module already started: that file stays a readable LIST of nav entries,
 * and everything that is not an entry lives beside it.
 *
 * A leaf — it imports one type and nothing else — and the definitions module
 * re-exports it, so no consumer changed.
 *
 * The map is separate from the group codes themselves on purpose. `SidebarGroup`
 * is a stable machine value that grouping logic and tests compare against; this
 * is how it is SPOKEN, in six languages. Merging them would put a code somewhere
 * a translator edits.
 *
 * **Every entry needs a DISTINCT key.** `Sidebar.partition` groups by the
 * translated label rather than by the code, so two codes pointing at one key
 * render as a single region — silently, and looking exactly like a group that
 * was never added.
 */
export const GROUP_LABEL_KEY: Record<SidebarGroup, string> = {
  AGENT: 'sidebar.groupAgent',
  SURFACES: 'sidebar.groupSurfaces',
  OBSERVABILITY: 'sidebar.groupObservability',
  ORG: 'sidebar.groupOrg',
};

/**
 * The A17 scannable cap — how many rows one sidebar GROUP may hold.
 *
 * Here rather than in a test because it was in TWO tests with TWO numbers, which
 * is how the 2026-09-01 walk-first × main merge failed: `sidebar-groups.test.ts`
 * had moved it 6 → 7 (both AGENT and OBSERVABILITY reached seven in that merge,
 * each from a program that had reasoned its own row — `plan/DEBT.md` D-148) while
 * `AppShell.test.tsx` still asserted 6 in its own literal. One of the two had to
 * be wrong, and nothing pointed at the pair.
 *
 * 7 is the TOP of A17's own stated range (7±2; ≤6 was one reading of it), so the
 * guard still has teeth: at 8 both tests fail, where before the number was a
 * comment in one file and a literal in another.
 *
 * The two tests count DIFFERENT things and both are right to: `sidebar-groups`
 * counts DEFINITIONS (a gated row is hidden for some workspaces, and the cap is
 * about the nav a fully-entitled operator sees), `AppShell` counts RENDERED
 * links (what a reader's eye actually meets). What they must not do is disagree
 * about the ceiling.
 */
export const MAX_ROWS_PER_GROUP = 7;
